import * as XLSX from 'xlsx';
import moment from 'moment';
import { getLinkedBuckets } from 'ontraccr-common/lib/Buckets';
import PDFTableExport from '../common/pdf/PDFExport/PDFTableExport';
import { isNullOrUndefined, getIdMap } from '../helpers/helpers';
import { parseCustomResponses } from '../boards/boards.helpers';
import { generateResponseMap } from '../forms/ResponderHelpers';
import { BASIC_TEXT_FIELD_TYPES } from '../forms/FormBuilder/FormFields/formFields.types';

import { BUCKET_TEMPLATE_PREFIX } from './projects.constants';

// CONSTANTS:
const getSummaryHeader = (t) => [
  'Estimated Labour Hours',
  'Estimated Labour Cost',
  'Average Labour Rate',
  `${t('Project')} Start Date`,
  `${t('Project')} End Date`,
];

const UNPHASED_COSTCODE_HEADER = [
  'NAME',
  'CODE',
  'DESCRIPTION',
  'HOURS',
  'START DATE',
  'END DATE',
];

const PHASED_COSTCODE_HEADER = [
  'PHASE',
  'DESCRIPTION',
  'NAME',
  'CODE',
  'DESCRIPTION',
  'HOURS',
  'START DATE',
  'END DATE',
];

const PROJECT_COLUMN_HEADERS = [
  { key: 'name', title: 'Name' },
  { key: 'number', title: 'Number' },
  { key: 'address', title: 'Address' },
  { key: 'division', title: 'Division' },
  { key: 'customer', title: 'Customer' },
];

const LABEL_HEADER = { key: 'labels', title: 'Labels' };

const EQUIPMENT_COLUMN_HEADERS = {
  id: 'Eq: ID',
  name: 'Eq: Name',
  status: 'Eq: Status',
  labels: 'Eq: Labels',
  buckets: 'Eq: Buckets',
  vendor: 'Eq: Vendor',
};

export const CUSTOM_FIELD_KEY_PREFIX = 'customField:';

const formatDate = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'number') return '';
  return moment(timestamp).format('MMM Do YYYY');
};

/**
 * Creates PDF export
 * @param {string} title
 * @param {number} startDate - millis
 * @param {number} endDate - millis
 * @param {object} companyImageURL
 * @param {array} infoTableBody
 * @param {array} unphasedTableBody
 * @param {array} phasedTableBody
 */
const exportPDF = async ({
  title,
  startDate,
  endDate,
  companyImageURL,
  infoTableBody,
  unphasedTableBody,
  phasedTableBody,
  t,
}) => {
  const SUMMARY_HEADER = getSummaryHeader(t);
  const pdfExportConstructor = new PDFTableExport();

  // Prepare Headers:
  const logoEntry = await PDFTableExport.getCompanyLogo(companyImageURL);
  const leftColumn = PDFTableExport.createColumnList({
    content: [
      { text: title, style: 'header' },
    ],
  });
  const rightColumn = PDFTableExport.createColumnList({
    content: [
      !isNullOrUndefined(startDate) && !isNullOrUndefined(endDate)
        ? { text: `${startDate} - ${endDate}`, style: 'header' }
        : { text: '', style: 'header' },
    ],
  });
  const header = PDFTableExport.createHeader({
    useLogo: true,
    logoEntry,
    columns: [leftColumn, rightColumn],
  });

  // Prepare Info Table:
  const adjustedInfoHeader = SUMMARY_HEADER.map((text) => ({ text, style: 'tableHeader' }));
  const infoTable = PDFTableExport.createTable({
    widths: Array(adjustedInfoHeader.length).fill('auto'),
    header: adjustedInfoHeader,
    rows: infoTableBody,
  });

  // Prepare Unphased Costcode Table:
  const adjustedUnphasedHeader = UNPHASED_COSTCODE_HEADER.map((text) => ({ text, style: 'tableHeader' }));
  const uphasedTable = PDFTableExport.createTable({
    widths: Array(adjustedUnphasedHeader.length).fill('auto'),
    header: adjustedUnphasedHeader,
    rows: unphasedTableBody,
  });

  // Prepare Phased Costcode Table:
  const adjustedPhasedHeader = PHASED_COSTCODE_HEADER.map((text) => ({ text, style: 'tableHeader' }));
  const phasedTable = PDFTableExport.createTable({
    widths: Array(adjustedPhasedHeader.length).fill('auto'),
    header: adjustedPhasedHeader,
    rows: phasedTableBody,
  });

  // Export PDF:
  pdfExportConstructor.export({
    name: title,
    header,
    body: [
      infoTable,
      uphasedTable,
      phasedTable,
    ],
  });
};

/**
 * Creates Excel Export
 * @param {string} title
 * @param {string} hoursStr
 * @param {number} startDate
 * @param {number} endDate
 * @param {string} costStr
 * @param {string} rateStr
 * @param {array} formattedUnphased
 * @param {array} formattedPhased
 */
const exportExcel = ({
  title,
  hoursStr,
  startDate,
  endDate,
  costStr,
  rateStr,
  formattedUnphased,
  formattedPhased,
  t,
}) => {
  let rows = [
    ['Estimated Labour Hours', hoursStr, '', `${t('Project')} Start Date`, isNullOrUndefined(startDate) ? '' : formatDate(startDate)],
    ['Estimated Labour Cost', costStr, '', `${t('Project')} End Date`, isNullOrUndefined(endDate) ? '' : formatDate(endDate)],
    ['Average Labour Rate', rateStr],
    [],
    ['UNPHASED COST CODES'],
    [],
    UNPHASED_COSTCODE_HEADER,
  ];
  rows = rows.concat(formattedUnphased);
  rows = rows.concat([
    [],
    ['PHASED COST CODES'],
    [],
    PHASED_COSTCODE_HEADER,
  ]);
  rows = rows.concat(formattedPhased);
  rows.push([]);
  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  // Sheet name cannot exceed 31 char.
  XLSX.utils.book_append_sheet(workbook, sheet, title.slice(0, 31));
  XLSX.writeFile(workbook, `${title}.xlsx`);
};

/**
 * Build list of exportable custom field columns from equipment types.
 */
export function getEquipmentCustomFields(equipmentTypes = []) {
  const columns = [];

  equipmentTypes.forEach((eqType) => {
    const customFields = eqType?.customFields?.fields ?? [];
    customFields.forEach((field) => {
      const {
        id,
        type: fieldType,
        selectedType,
      } = field;
      const type = fieldType ?? selectedType;

      if (!BASIC_TEXT_FIELD_TYPES.has(type)) return;
      columns.push({
        key: `${CUSTOM_FIELD_KEY_PREFIX}${id}`,
        equipmentTypeName: eqType?.name ?? '',
        ...field,
      });
    });
  });
  return columns;
}

const getProjectCells = ({
  project,
  columnsInOrder,
  divisions,
  projectToLabel,
  labelMap,
  bucketTemplateProjectMap,
  bucketMap,
}) => {
  const {
    id: projectId, name, number, address, divisionId, customer,
  } = project;
  const divisionName = divisions[divisionId]?.name ?? '';
  const labelsValue = (projectToLabel[projectId] ?? [])
    .map((labelId) => labelMap[labelId]?.title)
    .join(', ');

  const cellByKey = {
    name: name ?? '',
    number: number ?? '',
    address: address ?? '',
    division: divisionName,
    customer: customer ?? '',
    labels: labelsValue,
  };
  Object.keys(bucketTemplateProjectMap).forEach((bucketTemplateId) => {
    const {
      projectMap: projectToBucketsMap,
    } = bucketTemplateProjectMap[bucketTemplateId];
    const projectBuckets = projectToBucketsMap[projectId] ?? [];
    const bucketNames = projectBuckets.map((bucket) => bucketMap[bucket.id]?.name).join(', ');
    cellByKey[`${BUCKET_TEMPLATE_PREFIX}${bucketTemplateId}`] = bucketNames;
  });
  return columnsInOrder.map((key) => cellByKey[key] ?? '');
};

const getEquipmentCells = ({
  equipment,
  equipmentColumnKeys,
  buckets,
  bucketMap,
  vendors,
  customFieldColumnMap,
  equipmentCustomDataMap = {},
  equipmentCustomFieldMap = {},
  projectIdMap = {},
  settings = {},
}) => {
  if (!equipment) {
    return equipmentColumnKeys.map(() => '');
  }

  const { upstreamBucketIds = [] } = getLinkedBuckets(buckets, [equipment.id], ['equipment']);
  const {
    code, name, status, labels = [], vendorId,
  } = equipment;
  const labelsValue = labels
    .map(({ title }) => title)
    .join(', ');
  const bucketsValue = upstreamBucketIds
    .filter((bid) => bid in bucketMap)
    .map((bid) => `${bucketMap[bid]?.name} (${bucketMap[bid]?.templateName})`)
    .join(', ');
  const vendorName = vendors[vendorId]?.name ?? '';

  const cellByKey = {
    id: code ?? '',
    name: name ?? '',
    status: status ?? '',
    labels: labelsValue,
    buckets: bucketsValue,
    vendor: vendorName,
  };

  const customDataEntry = equipmentCustomDataMap[equipment.id];
  const responsesByFieldId = generateResponseMap(customDataEntry?.sections);

  return equipmentColumnKeys.map((key) => {
    if (key in cellByKey) return cellByKey[key] ?? '';
    if (!key.startsWith(CUSTOM_FIELD_KEY_PREFIX)) return '';

    const colDef = customFieldColumnMap.get(key);
    if (!colDef) return '';

    const { id: fieldId } = colDef;
    const fieldDef = equipmentCustomFieldMap[fieldId] ?? null;
    const response = responsesByFieldId[fieldId];
    return parseCustomResponses({
      field: { ...fieldDef, response },
      projectIdMap,
      settings,
    });
  });
};

const getProjectExportRows = ({
  headers,
  projects,
  columnsInOrder,
  includeEquipment,
  equipmentColumnKeys,
  divisions,
  projectToLabel,
  labels,
  buckets,
  bucketMap,
  bucketTemplateProjectMap,
  vendors,
  equipmentTypes = [],
  equipmentCustomFields = [],
  equipmentCustomDataMap = {},
  settings = {},
}) => {
  const rows = [headers];
  const projectLabels = labels.filter(({ type }) => type === 'projects');
  const labelMap = getIdMap(projectLabels);
  const projectIdMap = getIdMap(projects);
  const equipmentCustomFieldMap = getIdMap(
    equipmentTypes.flatMap((et) => et?.customFields?.fields ?? []),
  );
  const customFieldColumnMap = new Map(
    equipmentCustomFields.map((col) => [col.key, col]),
  );
  const equipmentCellParams = {
    equipmentColumnKeys,
    buckets,
    bucketMap,
    vendors,
    customFieldColumnMap,
    equipmentCustomDataMap,
    equipmentCustomFieldMap,
    projectIdMap,
    settings,
  };

  if (includeEquipment && equipmentColumnKeys.length > 0) {
    projects.forEach((project) => {
      const projectCells = getProjectCells({
        project,
        columnsInOrder,
        divisions,
        projectToLabel,
        labelMap,
        bucketMap,
        bucketTemplateProjectMap,
      });
      const equipments = project.equipment || [];
      if (equipments.length > 0) {
        equipments.forEach((equipment) => {
          rows.push([...projectCells, ...getEquipmentCells({
            equipment, ...equipmentCellParams,
          })]);
        });
      } else {
        rows.push([...projectCells, ...getEquipmentCells({
          equipment: null, ...equipmentCellParams,
        })]);
      }
    });
  } else {
    projects.forEach((project) => {
      rows.push(getProjectCells({
        project,
        columnsInOrder,
        divisions,
        projectToLabel,
        labelMap,
        bucketMap,
        bucketTemplateProjectMap,
      }));
    });
  }

  return rows;
};

/** Build key -> header title map for equipment columns */
function getEquipmentHeaderByKey(equipmentCustomFields = []) {
  const map = new Map(
    equipmentCustomFields.map(({ key, title }) => [key, title]),
  );
  return (key) => {
    if (key in EQUIPMENT_COLUMN_HEADERS) return EQUIPMENT_COLUMN_HEADERS[key];
    if (key.startsWith(CUSTOM_FIELD_KEY_PREFIX)) return map.get(key);
    return key;
  };
}

const EQUIPMENT_CUSTOM_DATA_BATCH_SIZE = 200;

async function getProjectEquipmentCustomDataMap({
  projects,
  includeEquipment,
  getEquipmentCustomDataMap,
}) {
  if (!includeEquipment || projects.length === 0 || !getEquipmentCustomDataMap) return {};

  const equipmentIds = [...new Set(
    projects.flatMap(({ equipment }) => equipment.map((eq) => eq.id)),
  )];

  const customDataMap = {};
  for (let i = 0; i < equipmentIds.length; i += EQUIPMENT_CUSTOM_DATA_BATCH_SIZE) {
    const batch = equipmentIds.slice(i, i + EQUIPMENT_CUSTOM_DATA_BATCH_SIZE);
    // eslint-disable-next-line no-await-in-loop
    const data = await getEquipmentCustomDataMap({ ids: batch });
    Object.assign(customDataMap, data?.customDataMap ?? {});
  }
  return customDataMap;
}

/**
 * Export project list to Excel with selected columns.
 * When includeEquipment is true, each equipment gets its own row with project info repeated.
 * @param {Array} projects - List of project objects
 * @param {string[]} columnKeys - Selected project column keys
 * @param {boolean} includeEquipment - If true, one row per equipment
 * @param {string[]} equipmentColumnKeys - Equipment column keys
 * @param {object} divisions - Map of divisionId -> { name }
 * @param {object} projectToLabel - Map of projectId -> labelId[]
 * @param {Array} labels - All labels (used for type 'projects')
 * @param {object} vendors - Map of vendorId -> { name }
 * @param {Array} buckets - Bucket objects for equipment upstreamBucketIds
 * @param {Array} equipmentTypes - Equipment types (for custom field definitions)
 * @param {Function} getEquipmentCustomDataMap - call to fetch equipment custom data
 */
export async function exportProjectsToExcel({
  projects = [],
  columnKeys = [],
  includeEquipment = false,
  equipmentColumnKeys = [],
  divisions = {},
  projectToLabel = {},
  labels = [],
  vendors = {},
  buckets = [],
  bucketMap,
  bucketTemplateProjectMap,
  equipmentTypes = [],
  getEquipmentCustomDataMap,
  settings = {},
}) {
  const colKeySet = new Set(columnKeys);
  const equipmentCustomFields = getEquipmentCustomFields(equipmentTypes);

  const projectHeaders = [];
  const columnsInOrder = [];
  PROJECT_COLUMN_HEADERS.forEach(({ key, title }) => {
    if (colKeySet.has(key)) {
      projectHeaders.push(title);
      columnsInOrder.push(key);
    }
  });

  Object.keys(bucketTemplateProjectMap).forEach((bucketTemplateId) => {
    const { name: bucketTemplateName } = bucketTemplateProjectMap[bucketTemplateId];
    if (colKeySet.has(`${BUCKET_TEMPLATE_PREFIX}${bucketTemplateId}`)) {
      projectHeaders.push(bucketTemplateName);
      columnsInOrder.push(`${BUCKET_TEMPLATE_PREFIX}${bucketTemplateId}`);
    }
  });

  if (colKeySet.has(LABEL_HEADER.key)) {
    projectHeaders.push(LABEL_HEADER.title);
    columnsInOrder.push(LABEL_HEADER.key);
  }

  const getEquipmentHeader = getEquipmentHeaderByKey(equipmentCustomFields);
  const equipmentHeaders = includeEquipment
    ? equipmentColumnKeys.map(getEquipmentHeader)
    : [];
  const headers = [...projectHeaders, ...equipmentHeaders];

  const equipmentCustomDataMap = await getProjectEquipmentCustomDataMap({
    projects,
    includeEquipment,
    getEquipmentCustomDataMap,
  });

  const rows = getProjectExportRows({
    headers,
    projects,
    columnsInOrder,
    includeEquipment,
    equipmentColumnKeys,
    divisions,
    projectToLabel,
    labels,
    vendors,
    buckets,
    bucketMap,
    bucketTemplateProjectMap,
    equipmentTypes,
    equipmentCustomFields,
    equipmentCustomDataMap,
    settings,
  });

  const workbook = XLSX.utils.book_new();
  const sheet = XLSX.utils.aoa_to_sheet(rows);
  XLSX.utils.book_append_sheet(workbook, sheet, 'Projects');
  XLSX.writeFile(workbook, 'Projects-export.xlsx');
}

export default function projectExport({
  isPDF,
  startDate,
  endDate,
  companyImageURL,
  name,
  number,
  labourCost,
  labourHours,
  unphasedCostcodes = [],
  phaseCodes = [],
  t,
}) {
  const title = `${number} ${name.replace(/[/*[\]]/g, '')} ${t('Project')} Details`;
  const hoursStr = labourHours || 'N/A';
  const costStr = !isNullOrUndefined(labourCost) ? `$ ${labourCost}` : 'N/A';
  const rateStr = labourHours && labourCost ? `$ ${(labourCost / labourHours).toFixed(2)} / hr` : 'N/A';
  const projectInfo = [
    hoursStr,
    costStr,
    rateStr,
    isNullOrUndefined(startDate) ? '' : startDate,
    isNullOrUndefined(endDate) ? '' : endDate,
  ];

  const formattedUnphased = unphasedCostcodes.map(({
    name: ccName, code, description, hours = '', startDate = '', endDate = '',
  }) => [ccName, code, description, isNullOrUndefined(hours) ? '' : hours, formatDate(startDate), formatDate(endDate)]);

  let formattedPhased = [];
  phaseCodes.forEach((phase) => {
    const {
      name: phaseName,
      description: phaseDescription,
      costcodes = [],
    } = phase;
    formattedPhased = formattedPhased.concat(
      costcodes.map(({
        name: ccName, code, description, hours = '', startDate = '', endDate = '',
      }) => [phaseName, phaseDescription, ccName, code, description, isNullOrUndefined(hours) ? '' : hours, formatDate(startDate), formatDate(endDate)]),
    );
  });

  if (isPDF) {
    exportPDF({
      title,
      startDate,
      endDate,
      companyImageURL,
      infoTableBody: [projectInfo],
      unphasedTableBody: formattedUnphased,
      phasedTableBody: formattedPhased,
      t,
    });
  } else {
    exportExcel({
      title,
      hoursStr,
      startDate,
      endDate,
      costStr,
      rateStr,
      formattedUnphased,
      formattedPhased,
      t,
    });
  }
}
